import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmployeeCombobox } from '@/components/common/EmployeeCombobox';
import { JOB_CATEGORIES, DEFAULT_JOB_CATEGORY, jobCategoryLabel } from '@/constants/jobCategories';
import { Briefcase, Loader2, Check, ExternalLink } from 'lucide-react';

/**
 * Convert an estimate or an invoice into a Job without leaving the project.
 * TEMA B2 (Alian): shows every item with its conversion state BEFORE
 * confirming — items already converted (from this dialog, the Invoices page,
 * or the estimate itself) are locked with a link to the existing job, so the
 * same item can never generate a second one. Two distinct items of the same
 * type each still get their own job (criterio "por item de origen").
 */
export default function ConvertToJobDialog({ open, onClose, source, onDone }) {
  const [users, setUsers] = useState([]);
  const [assignedTo, setAssignedTo] = useState('');
  const [category, setCategory] = useState(DEFAULT_JOB_CATEGORY);
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [doc, setDoc] = useState(null);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!open || !source) return;
    setAssignedTo(''); setCategory(DEFAULT_JOB_CATEGORY); setCategoryTouched(false); setTitle(''); setDoc(null); setSelected([]);
    api.get('/auth/users').then(r => setUsers(r.data || [])).catch(() => setUsers([]));
    setLoadingDoc(true);
    const path = source.kind === 'invoice' ? `/invoices/${source.id}` : `/estimates/${source.id}`;
    api.get(path).then(r => {
      setDoc(r.data);
      const converted = r.data.converted_items || {};
      const notConverted = (r.data.items || []).map((_, i) => i).filter(i => !converted[String(i)]);
      setSelected(notConverted);
      // TEMA A (Site Inspection): si el estimado/factura de origen es una
      // inspección de sitio, preseleccionar esa categoría de trabajo (Alian
      // no debería tener que recordar cambiarla a mano cada vez).
      if (r.data.well_info?.work_type === 'site_inspection') {
        setCategory('site_inspection');
      }
    }).catch(() => toast.error('No se pudo cargar el documento')).finally(() => setLoadingDoc(false));
  }, [open, source]);

  const toggleItem = (idx) => {
    setSelected(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  const submit = async () => {
    if (!source || !doc) return;
    if (selected.length === 0) {
      toast.error('Selecciona al menos un item');
      return;
    }
    setSaving(true);
    try {
      const path = source.kind === 'invoice' ? `/invoices/${source.id}/convert-to-job` : `/estimates/${source.id}/convert-to-job`;
      const res = await api.post(path, {
        selected_item_indices: selected, assigned_to: assignedTo, job_category: category, job_title: title,
      });
      const job = res.data.job;
      toast.success(`Trabajo ${job.job_number} creado`);
      onDone?.(job);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al convertir');
    } finally {
      setSaving(false);
    }
  };

  const converted = doc?.converted_items || {};
  const items = doc?.items || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="project-convert-to-job-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow'] flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-violet-500" /> Convert to Job
          </DialogTitle>
        </DialogHeader>
        {source && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-violet-50 border border-violet-200">
              <p className="text-sm font-semibold text-violet-900" data-testid="convert-source-number">{source.number}</p>
              <p className="text-xs text-violet-600">{source.kind === 'invoice' ? 'Invoice' : 'Estimate'}{source.subtitle ? ` · ${source.subtitle}` : ''}</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700">Select Items for this Job</Label>
              {loadingDoc ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5">
                  {items.map((item, idx) => {
                    const info = converted[String(idx)];
                    const isSelected = selected.includes(idx);
                    return (
                      <div
                        key={idx}
                        data-testid={`project-convert-item-${idx}`}
                        onClick={() => !info && toggleItem(idx)}
                        className={`flex items-center gap-2 p-2 rounded-lg border transition-colors cursor-pointer ${
                          info ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                            : isSelected ? 'bg-violet-50 border-violet-400' : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Checkbox
                          data-testid={`project-convert-checkbox-${idx}`}
                          checked={isSelected}
                          disabled={!!info}
                          onCheckedChange={() => !info && toggleItem(idx)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-medium truncate ${info ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                            {item.name || item.description || `Item ${idx + 1}`}
                          </p>
                          {info && (
                            <button
                              type="button"
                              data-testid={`project-convert-existing-job-${idx}`}
                              onClick={(e) => { e.stopPropagation(); window.open(`/jobs/${info.job_id}`, '_blank'); }}
                              className="text-[11px] text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1"
                            >
                              <Check className="w-2.5 h-2.5" /> Ya es {info.job_number}
                              {info.job_type && ` · ${jobCategoryLabel(info.job_type)}`}
                              <ExternalLink className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {items.length > 0 && items.every((_, idx) => converted[String(idx)]) && (
                    <p className="text-xs text-amber-600 text-center py-1">Todos los items ya fueron convertidos a trabajos.</p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Assign to</Label>
              {/* Fix D (2026-08-31): al elegir la persona se precarga SU categoria
                  por defecto (users.default_job_category, configurable en Settings >
                  Users). Alian: "cuando yo ponga Reynaldo Perez Pump Installer, aca
                  abajo me salga al momento Pump Installer". Es una SUGERENCIA: el
                  selector de categoria sigue editable y si el usuario ya la toco a
                  mano no se pisa. */}
              <EmployeeCombobox
                users={users}
                value={assignedTo}
                onChange={(userId) => {
                  setAssignedTo(userId);
                  const u = users.find(x => x.id === userId);
                  if (u?.default_job_category && !categoryTouched) {
                    setCategory(u.default_job_category);
                  }
                }}
                testId="project-convert-assigned-to"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Job Category</Label>
              <Select value={category} onValueChange={(v) => { setCategoryTouched(true); setCategory(v); }}>
                <SelectTrigger data-testid="project-convert-job-category" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {JOB_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Job Title (optional)</Label>
              <Input data-testid="project-convert-job-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Auto-generated if empty" className="h-11" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                type="button"
                data-testid="project-confirm-convert-to-job"
                onClick={submit}
                disabled={saving || !assignedTo || selected.length === 0}
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <><Briefcase className="w-4 h-4 mr-2" />Create Job ({selected.length})</>}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
